import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Card,
    Row,
    Col,
    Select,
    Button,
    Table,
    Typography,
    message,
    Spin,
    Empty,
    Tag,
    Statistic,
    Divider,
    Alert,
} from "antd";

import {
    SafetyCertificateOutlined,
    DownloadOutlined,
    TeamOutlined,
    UserOutlined,
    ApartmentOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    FileProtectOutlined,
    TrophyOutlined,
    CrownOutlined,
} from "@ant-design/icons";

import api from "../../api/api";
import { useAcademicYear } from "../../AcademicYearContext";
import GraduateCertificatePDF from "./GraduateCertificatePDF";

const { Title, Text } = Typography;
const { Option } = Select;

const getDisplayValue = (value) => {
    if (!value) {
        return "";
    }

    if (typeof value === "object") {
        return (
            value.name ||
            value.title ||
            ""
        );
    }

    return value;
};

const getGradeClassLabel = (item) => {
    const className =
        getDisplayValue(item?.name);

    const division =
        getDisplayValue(item?.division);

    if (division) {
        return `${className} - ${division}`;
    }

    return className;
};

const CertificateDashboard = () => {
    const {
        selectedYear,
    } = useAcademicYear();

    const [
        gradeClasses,
        setGradeClasses,
    ] = useState([]);

    const [
        selectedClass,
        setSelectedClass,
    ] = useState(null);

    const [
        students,
        setStudents,
    ] = useState([]);

    const [
        selectedStudent,
        setSelectedStudent,
    ] = useState(null);

    const [
        certificates,
        setCertificates,
    ] = useState([]);

    const [
        loading,
        setLoading,
    ] = useState(false);

    const [
        studentLoading,
        setStudentLoading,
    ] = useState(false);

    const fetchGradeClasses =
        async () => {
            try {
                const response =
                    await api.get(
                        "/api/users/grade-classes/"
                    );

                const data =
                    response.data.results ||
                    response.data ||
                    [];

                setGradeClasses(data);
            } catch (error) {
                console.error(error);

                message.error(
                    "Unable to load training programs."
                );
            }
        };

    const fetchCertificates =
        async () => {
            try {
                setLoading(true);

                const response =
                    await api.get(
                        "/certificates/",
                        {
                            params: {
                                academic_year:
                                    selectedYear?.id,
                            },
                        }
                    );

                setCertificates(
                    response.data.results ||
                    response.data ||
                    []
                );
            } catch (error) {
                console.error(error);

                message.error(
                    "Unable to load certificate history."
                );
            } finally {
                setLoading(false);
            }
        };

    const fetchStudents =
        async (classId) => {
            if (!classId) {
                setStudents([]);
                return;
            }

            try {
                setStudentLoading(true);

                const response =
                    await api.get(
                        "graduated-students/",
                        {
                            params: {
                                grade_class:
                                    classId,
                                academic_year:
                                    selectedYear?.id,
                            },
                        }
                    );

                setStudents(
                    response.data.results ||
                    response.data ||
                    []
                );
            } catch (error) {
                console.error(error);

                setStudents([]);

                message.error(
                    "Unable to load graduated students."
                );
            } finally {
                setStudentLoading(false);
            }
        };

    useEffect(() => {
        fetchGradeClasses();
    }, []);

    useEffect(() => {
        if (selectedYear) {
            fetchCertificates();

            setSelectedClass(null);
            setSelectedStudent(null);
            setStudents([]);
        }
    }, [selectedYear]);

    const handleClassChange =
        (classId) => {
            const selected =
                gradeClasses.find(
                    (item) =>
                        item.id === classId
                );

            setSelectedClass(
                selected || null
            );

            setSelectedStudent(null);
            setStudents([]);

            fetchStudents(classId);
        };

    const generateStudentCertificate =
        () => {
            if (!selectedClass) {
                message.warning(
                    "Please select a training program or class."
                );

                return;
            }

            if (!selectedStudent) {
                message.warning(
                    "Please select a graduate."
                );

                return;
            }

            const student =
                students.find(
                    (item) =>
                        item.id ===
                        selectedStudent
                );

            if (!student) {
                message.error(
                    "Graduate could not be found."
                );

                return;
            }

            GraduateCertificatePDF.downloadSingle(
                student,
                selectedClass,
                selectedYear
            );
        };

    const generateClassCertificates =
        () => {
            if (!selectedClass) {
                message.warning(
                    "Please select a training program or class."
                );

                return;
            }

            if (students.length === 0) {
                message.warning(
                    "No graduated students were found."
                );

                return;
            }

            GraduateCertificatePDF.downloadMultiple(
                students,
                selectedClass,
                selectedYear
            );
        };

    const getStudentName =
        (student) => {
            return (
                student?.full_name ||
                student?.student_name ||
                getDisplayValue(
                    student?.name
                ) ||
                "-"
            );
        };

    const getAcademicYearName =
        (record) => {
            if (
                record?.academic_year_name
            ) {
                return record.academic_year_name;
            }

            if (record?.academic_year) {
                return getDisplayValue(
                    record.academic_year
                );
            }

            return "-";
        };

    const columns = useMemo(
        () => [
            {
                title: "Graduate",
                key: "student_name",

                render: (_, record) => (
                    <div className="cayep-table-person">
                        <div className="cayep-table-avatar">
                            <UserOutlined />
                        </div>

                        <div>
                            <div className="cayep-table-primary">
                                {getStudentName(
                                    record
                                )}
                            </div>

                            <div className="cayep-table-secondary">
                                Leadership Training Graduate
                            </div>
                        </div>
                    </div>
                ),
            },

            {
                title: "Program / Class",
                key: "grade_class",

                render: (_, record) => {
                    let label = "-";

                    if (
                        record?.grade_class_name
                    ) {
                        label =
                            record.grade_class_name;
                    } else if (
                        record?.grade_class
                    ) {
                        label =
                            getGradeClassLabel(
                                record.grade_class
                            );
                    }

                    return (
                        <div className="cayep-program-cell">
                            <ApartmentOutlined />

                            <span>
                                {label}
                            </span>
                        </div>
                    );
                },
            },

            {
                title: "Training Year",
                key: "academic_year",

                render: (_, record) => (
                    <div className="cayep-year-cell">
                        <CalendarOutlined />

                        <span>
                            {getAcademicYearName(
                                record
                            )}
                        </span>
                    </div>
                ),
            },

            {
                title: "Generated",
                dataIndex: "created_at",

                render: (date) => {
                    if (!date) {
                        return "-";
                    }

                    return (
                        <span className="cayep-date">
                            {new Date(
                                date
                            ).toLocaleDateString(
                                undefined,
                                {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                }
                            )}
                        </span>
                    );
                },
            },

            {
                title: "Status",
                key: "status",

                render: () => (
                    <Tag
                        className="cayep-generated-tag"
                        icon={
                            <CheckCircleOutlined />
                        }
                    >
                        Generated
                    </Tag>
                ),
            },
        ],
        []
    );

    return (
        <>
            <style>
                {`
                    .certificate-dashboard {
                        --cayep-nav-navy: #062A4B;
                        --cayep-nav-blue: #063A6B;
                        --cayep-nav-black: #0A0F12;
                        --cayep-nav-gold: #F6C944;
                        --cayep-nav-yellow: #EFA81C;
                        --cayep-nav-orange: #C0750D;
                        --cayep-nav-green: #4FAE22;
                        --cayep-nav-olive: #5F6622;
                        --cayep-nav-sky: #0F6DAB;
                        --cayep-nav-white: #F0EDEA;
                        --cayep-nav-gray: #C4C7C7;
                        --cayep-nav-antique: #CBA454;

                        --nav-white: #ffffff;
                        --nav-text: #142536;
                        --nav-muted: #6b7280;
                        --nav-border: #e7ebee;
                        --nav-soft: #f7f9fa;

                        --nav-shadow:
                            0 12px 35px
                            rgba(6, 42, 75, 0.10);

                        --nav-transition: 220ms ease;

                        width: 100%;
                        min-height: 100%;
                        padding: 28px;

                        background:
                            linear-gradient(
                                180deg,
                                #f8fafb 0%,
                                #f1f5f7 100%
                            );

                        color: var(--nav-text);

                        box-sizing: border-box;
                    }

                    .certificate-dashboard *,
                    .certificate-dashboard *::before,
                    .certificate-dashboard *::after {
                        box-sizing: border-box;
                    }

                    .cayep-dashboard-shell {
                        width: 100%;
                        max-width: 1600px;
                        margin: 0 auto;
                    }

                    /* HERO */

                    .cayep-hero {
                        position: relative;
                        overflow: hidden;

                        min-height: 245px;

                        margin-bottom: 24px;

                        border-radius: 20px;

                        background:
                            linear-gradient(
                                120deg,
                                var(--cayep-nav-navy) 0%,
                                var(--cayep-nav-blue) 54%,
                                var(--cayep-nav-sky) 100%
                            );

                        box-shadow:
                            0 20px 50px
                            rgba(6, 42, 75, 0.18);
                    }

                    .cayep-hero::before {
                        content: "";

                        position: absolute;

                        width: 480px;
                        height: 480px;

                        right: -210px;
                        top: -285px;

                        border-radius: 50%;

                        border:
                            1px solid
                            rgba(246, 201, 68, 0.25);

                        box-shadow:
                            0 0 0 40px
                            rgba(246, 201, 68, 0.025),
                            0 0 0 80px
                            rgba(246, 201, 68, 0.018);
                    }

                    .cayep-hero::after {
                        content: "";

                        position: absolute;

                        width: 360px;
                        height: 360px;

                        left: -230px;
                        bottom: -270px;

                        border-radius: 50%;

                        border:
                            1px solid
                            rgba(240, 237, 234, 0.10);
                    }

                    .cayep-hero-pattern {
                        position: absolute;
                        inset: 0;

                        opacity: 0.28;

                        background-image:
                            linear-gradient(
                                135deg,
                                rgba(255,255,255,0.045)
                                25%,
                                transparent 25%
                            ),
                            linear-gradient(
                                315deg,
                                rgba(255,255,255,0.025)
                                25%,
                                transparent 25%
                            );

                        background-size:
                            38px 38px;
                    }

                    .cayep-hero-content {
                        position: relative;
                        z-index: 3;

                        display: flex;

                        align-items: center;

                        gap: 28px;

                        min-height: 245px;

                        padding:
                            40px 44px;
                    }

                    .cayep-brand-mark {
                        width: 86px;
                        height: 86px;

                        flex: 0 0 86px;

                        display: flex;

                        align-items: center;
                        justify-content: center;

                        border-radius: 20px;

                        color:
                            var(--cayep-nav-navy);

                        background:
                            linear-gradient(
                                145deg,
                                var(--cayep-nav-gold),
                                var(--cayep-nav-yellow)
                            );

                        font-size: 40px;

                        box-shadow:
                            0 14px 28px
                            rgba(0,0,0,0.20),
                            inset 0 1px 0
                            rgba(255,255,255,0.45);
                    }

                    .cayep-hero-copy {
                        max-width: 900px;
                    }

                    .cayep-eyebrow {
                        display: inline-flex;

                        margin-bottom: 8px;

                        color:
                            var(--cayep-nav-gold);

                        font-size: 11px;

                        font-weight: 800;

                        letter-spacing: 2px;

                        text-transform:
                            uppercase;
                    }

                    .cayep-hero-title.ant-typography {
                        margin:
                            0 0 9px;

                        color: #ffffff;

                        font-size:
                            clamp(
                                28px,
                                3.4vw,
                                43px
                            );

                        line-height: 1.12;

                        font-weight: 800;

                        letter-spacing:
                            -0.8px;
                    }

                    .cayep-hero-description {
                        display: block;

                        max-width: 780px;

                        color:
                            rgba(
                                255,
                                255,
                                255,
                                0.78
                            ) !important;

                        font-size: 14px;

                        line-height: 1.75;
                    }

                    .cayep-hero-meta {
                        display: flex;

                        align-items: center;

                        flex-wrap: wrap;

                        gap: 13px;

                        margin-top: 22px;

                        color:
                            rgba(
                                255,
                                255,
                                255,
                                0.76
                            );

                        font-size: 11px;
                    }

                    .cayep-hero-meta span {
                        display: inline-flex;

                        align-items: center;

                        gap: 7px;
                    }

                    .cayep-hero-meta svg {
                        color:
                            var(--cayep-nav-gold);
                    }

                    .cayep-hero-meta strong {
                        color: #ffffff;
                    }

                    .cayep-meta-divider {
                        width: 4px;
                        height: 4px;

                        border-radius: 50%;

                        background:
                            var(--cayep-nav-antique);
                    }

                    /* STATISTICS */

                    .cayep-stat-grid {
                        display: grid;

                        grid-template-columns:
                            repeat(3, 1fr);

                        gap: 20px;

                        margin-bottom: 24px;
                    }

                    .cayep-stat-card.ant-card {
                        overflow: hidden;

                        border:
                            1px solid
                            var(--nav-border);

                        border-radius: 16px;

                        background:
                            var(--nav-white);

                        box-shadow:
                            var(--nav-shadow);

                        transition:
                            transform
                            var(--nav-transition),
                            box-shadow
                            var(--nav-transition);
                    }

                    .cayep-stat-card.ant-card:hover {
                        transform:
                            translateY(-2px);

                        box-shadow:
                            0 17px 42px
                            rgba(6,42,75,0.14);
                    }

                    .cayep-stat-card
                        .ant-card-body {
                        position: relative;

                        padding: 21px 23px;
                    }

                    .cayep-stat-card
                        .ant-card-body::after {
                        content: "";

                        position: absolute;

                        left: 0;
                        right: 0;
                        bottom: 0;

                        height: 3px;

                        background:
                            linear-gradient(
                                90deg,
                                var(--cayep-nav-gold),
                                var(--cayep-nav-orange)
                            );

                        opacity: 0.8;
                    }

                    .cayep-stat-card
                        .ant-statistic {
                        display: flex;

                        align-items: center;

                        gap: 14px;
                    }

                    .cayep-stat-card
                        .ant-statistic-title {
                        color:
                            var(--nav-muted);

                        font-size: 11px;

                        font-weight: 700;

                        margin-bottom: 3px;
                    }

                    .cayep-stat-card
                        .ant-statistic-content {
                        color:
                            var(--cayep-nav-navy);

                        font-size: 28px;

                        font-weight: 800;
                    }

                    .cayep-stat-card
                        .ant-statistic-content-prefix {
                        margin-right: 0;
                    }

                    .cayep-stat-icon {
                        width: 45px;
                        height: 45px;

                        display: inline-flex;

                        align-items: center;
                        justify-content: center;

                        border-radius: 13px;

                        font-size: 20px;
                    }

                    .cayep-stat-icon.gold {
                        color:
                            var(--cayep-nav-orange);

                        background:
                            rgba(
                                246,
                                201,
                                68,
                                0.18
                            );
                    }

                    .cayep-stat-icon.blue {
                        color:
                            var(--cayep-nav-sky);

                        background:
                            rgba(
                                15,
                                109,
                                171,
                                0.11
                            );
                    }

                    .cayep-stat-icon.green {
                        color:
                            var(--cayep-nav-green);

                        background:
                            rgba(
                                79,
                                174,
                                34,
                                0.11
                            );
                    }

                    .cayep-stat-note {
                        display: block;

                        margin-top: 8px;
                        margin-left: 59px;

                        color:
                            var(--nav-muted);

                        font-size: 10px;
                    }

                    /* MAIN CARDS */

                    .cayep-generator-card.ant-card,
                    .cayep-history-card.ant-card {
                        height: 100%;

                        border:
                            1px solid
                            var(--nav-border);

                        border-radius: 18px;

                        background:
                            var(--nav-white);

                        box-shadow:
                            var(--nav-shadow);
                    }

                    .cayep-generator-card
                        .ant-card-body,
                    .cayep-history-card
                        .ant-card-body {
                        padding: 28px;
                    }

                    .cayep-card-heading {
                        display: flex;

                        align-items:
                            flex-start;

                        gap: 14px;
                    }

                    .cayep-card-icon {
                        width: 45px;
                        height: 45px;

                        flex: 0 0 45px;

                        display: flex;

                        align-items: center;
                        justify-content: center;

                        border-radius: 12px;

                        color:
                            var(--cayep-nav-navy);

                        background:
                            linear-gradient(
                                145deg,
                                rgba(
                                    246,
                                    201,
                                    68,
                                    0.25
                                ),
                                rgba(
                                    203,
                                    164,
                                    84,
                                    0.13
                                )
                            );

                        font-size: 20px;
                    }

                    .cayep-card-heading
                        h4.ant-typography {
                        margin:
                            0 0 4px;

                        color:
                            var(--cayep-nav-navy);

                        font-size: 18px;

                        font-weight: 800;
                    }

                    .cayep-card-heading
                        .ant-typography:not(h4) {
                        color:
                            var(--nav-muted);

                        font-size: 11px;

                        line-height: 1.65;
                    }

                    .cayep-generator-card
                        .ant-divider,
                    .cayep-history-card
                        .ant-divider {
                        margin: 22px 0;

                        border-color:
                            var(--nav-border);
                    }

                    /* FORM */

                    .cayep-form-section {
                        margin-bottom: 19px;
                    }

                    .cayep-field-label {
                        display: block;

                        margin-bottom: 8px;

                        color:
                            var(--cayep-nav-navy);

                        font-size: 11px;

                        font-weight: 800;

                        letter-spacing:
                            0.2px;
                    }

                    .cayep-select {
                        width: 100%;
                    }

                    .cayep-select
                        .ant-select-selector {
                        min-height: 47px
                            !important;

                        border-radius:
                            10px !important;

                        border-color:
                            #d9e1e5 !important;

                        box-shadow:
                            none !important;

                        display: flex;

                        align-items:
                            center;

                        transition:
                            border-color
                            var(--nav-transition),
                            box-shadow
                            var(--nav-transition);
                    }

                    .cayep-select:hover
                        .ant-select-selector,
                    .cayep-select.ant-select-focused
                        .ant-select-selector {
                        border-color:
                            var(--cayep-nav-sky) !important;

                        box-shadow:
                            0 0 0 3px
                            rgba(
                                15,
                                109,
                                171,
                                0.08
                            ) !important;
                    }

                    .cayep-inline-empty {
                        margin-top: 8px;

                        padding:
                            9px 11px;

                        border-radius: 8px;

                        color:
                            var(--nav-muted);

                        background:
                            var(--nav-soft);

                        font-size: 10px;

                        line-height: 1.5;
                    }

                    /* ACTIONS */

                    .cayep-action-stack {
                        display: flex;

                        flex-direction:
                            column;

                        gap: 10px;

                        margin-top: 24px;
                    }

                    .cayep-primary-button.ant-btn {
                        height: 48px;

                        border: none;

                        border-radius: 10px;

                        background:
                            linear-gradient(
                                135deg,
                                var(--cayep-nav-blue),
                                var(--cayep-nav-navy)
                            );

                        box-shadow:
                            0 8px 20px
                            rgba(
                                6,
                                58,
                                107,
                                0.20
                            );

                        font-size: 12px;

                        font-weight: 800;
                    }

                    .cayep-primary-button.ant-btn:hover,
                    .cayep-primary-button.ant-btn:focus {
                        background:
                            linear-gradient(
                                135deg,
                                var(--cayep-nav-sky),
                                var(--cayep-nav-blue)
                            ) !important;
                    }

                    .cayep-secondary-button.ant-btn {
                        height: 48px;

                        border-radius: 10px;

                        border:
                            1px solid
                            #d8e0e4;

                        color:
                            var(--cayep-nav-navy);

                        background:
                            #ffffff;

                        font-size: 12px;

                        font-weight: 800;
                    }

                    .cayep-secondary-button.ant-btn:hover,
                    .cayep-secondary-button.ant-btn:focus {
                        color:
                            var(--cayep-nav-blue) !important;

                        border-color:
                            var(--cayep-nav-sky) !important;

                        background:
                            #f7fbfd !important;
                    }

                    .cayep-generator-notice.ant-alert {
                        margin-top: 21px;

                        border-radius: 11px;

                        border:
                            1px solid
                            rgba(
                                15,
                                109,
                                171,
                                0.14
                            );

                        background:
                            #f5f9fc;
                    }

                    .cayep-generator-notice
                        .ant-alert-message {
                        color:
                            var(--cayep-nav-navy);

                        font-size: 11px;

                        font-weight: 800;
                    }

                    .cayep-generator-notice
                        .ant-alert-description {
                        color:
                            var(--nav-muted);

                        font-size: 10px;

                        line-height: 1.6;
                    }

                    /* HISTORY */

                    .cayep-history-header {
                        display: flex;

                        align-items: center;

                        justify-content:
                            space-between;

                        gap: 20px;
                    }

                    .cayep-section-kicker {
                        margin-bottom: 5px;

                        color:
                            var(--cayep-nav-orange);

                        font-size: 9px;

                        font-weight: 900;

                        letter-spacing:
                            1.5px;

                        text-transform:
                            uppercase;
                    }

                    .cayep-history-header
                        h4.ant-typography {
                        margin:
                            0 0 4px;

                        color:
                            var(--cayep-nav-navy);

                        font-size: 20px;

                        font-weight: 800;
                    }

                    .cayep-history-header
                        .ant-typography:not(h4) {
                        color:
                            var(--nav-muted);

                        font-size: 11px;
                    }

                    .cayep-history-count {
                        min-width: 78px;

                        padding:
                            11px 14px;

                        border-radius: 12px;

                        text-align: center;

                        background:
                            linear-gradient(
                                145deg,
                                #f9fafb,
                                #f2f5f6
                            );

                        border:
                            1px solid
                            var(--nav-border);
                    }

                    .cayep-history-count span {
                        display: block;

                        color:
                            var(--cayep-nav-navy);

                        font-size: 21px;

                        font-weight: 900;

                        line-height: 1.1;
                    }

                    .cayep-history-count small {
                        display: block;

                        margin-top: 3px;

                        color:
                            var(--nav-muted);

                        font-size: 8px;

                        font-weight: 800;

                        text-transform:
                            uppercase;

                        letter-spacing:
                            0.8px;
                    }

                    /* TABLE */

                    .cayep-certificate-table
                        .ant-table {
                        background:
                            transparent;
                    }

                    .cayep-certificate-table
                        .ant-table-container {
                        overflow: hidden;

                        border:
                            1px solid
                            var(--nav-border);

                        border-radius: 12px;
                    }

                    .cayep-certificate-table
                        .ant-table-thead > tr > th {
                        padding:
                            13px 15px;

                        border-bottom:
                            1px solid
                            var(--nav-border);

                        color:
                            var(--nav-muted);

                        background:
                            linear-gradient(
                                180deg,
                                #fafbfc,
                                #f6f8f9
                            );

                        font-size: 9px;

                        font-weight: 900;

                        letter-spacing:
                            0.7px;

                        text-transform:
                            uppercase;
                    }

                    .cayep-certificate-table
                        .ant-table-tbody > tr > td {
                        padding:
                            14px 15px;

                        border-bottom:
                            1px solid
                            #edf0f2;

                        color:
                            var(--nav-text);

                        font-size: 11px;

                        background:
                            #ffffff;

                        transition:
                            background
                            var(--nav-transition);
                    }

                    .cayep-certificate-table
                        .ant-table-tbody > tr:hover > td {
                        background:
                            #f8fbfd !important;
                    }

                    .cayep-table-person {
                        display: flex;

                        align-items: center;

                        gap: 10px;
                    }

                    .cayep-table-avatar {
                        width: 35px;
                        height: 35px;

                        flex: 0 0 35px;

                        display: flex;

                        align-items: center;
                        justify-content: center;

                        border-radius: 9px;

                        color:
                            var(--cayep-nav-navy);

                        background:
                            rgba(
                                6,
                                58,
                                107,
                                0.08
                            );

                        font-size: 14px;
                    }

                    .cayep-table-primary {
                        color:
                            var(--cayep-nav-navy);

                        font-size: 11px;

                        font-weight: 800;
                    }

                    .cayep-table-secondary {
                        margin-top: 2px;

                        color:
                            var(--nav-muted);

                        font-size: 9px;
                    }

                    .cayep-program-cell,
                    .cayep-year-cell {
                        display: flex;

                        align-items: center;

                        gap: 8px;
                    }

                    .cayep-program-cell svg {
                        color:
                            var(--cayep-nav-sky);
                    }

                    .cayep-year-cell svg {
                        color:
                            var(--cayep-nav-antique);
                    }

                    .cayep-date {
                        color:
                            var(--nav-muted);

                        font-size: 10px;
                    }

                    .cayep-generated-tag.ant-tag {
                        margin: 0;

                        border:
                            1px solid
                            rgba(
                                79,
                                174,
                                34,
                                0.18
                            );

                        border-radius: 20px;

                        color:
                            #3d861f;

                        background:
                            rgba(
                                79,
                                174,
                                34,
                                0.09
                            );

                        font-size: 9px;

                        font-weight: 800;
                    }

                    /* EMPTY */

                    .cayep-table-empty {
                        display: flex;

                        align-items: center;

                        flex-direction:
                            column;

                        padding:
                            45px 20px;
                    }

                    .cayep-empty-icon {
                        width: 58px;
                        height: 58px;

                        display: flex;

                        align-items: center;
                        justify-content: center;

                        margin-bottom: 13px;

                        border-radius: 15px;

                        color:
                            var(--cayep-nav-navy);

                        background:
                            linear-gradient(
                                145deg,
                                rgba(
                                    6,
                                    58,
                                    107,
                                    0.09
                                ),
                                rgba(
                                    246,
                                    201,
                                    68,
                                    0.08
                                )
                            );

                        font-size: 23px;
                    }

                    .cayep-table-empty
                        .ant-typography {
                        display: block;

                        max-width: 370px;

                        text-align: center;

                        color:
                            var(--nav-muted);

                        font-size: 10px;

                        line-height: 1.65;
                    }

                    .cayep-table-empty
                        .ant-typography strong {
                        margin-bottom: 4px;

                        color:
                            var(--cayep-nav-navy);

                        font-size: 13px;
                    }

                    /* LOADING */

                    .cayep-loading {
                        min-height: 310px;

                        display: flex;

                        align-items: center;
                        justify-content: center;

                        flex-direction:
                            column;

                        gap: 13px;
                    }

                    .cayep-loading
                        .ant-typography {
                        color:
                            var(--nav-muted);

                        font-size: 11px;
                    }

                    .cayep-loading
                        .ant-spin-dot-item {
                        background:
                            var(--cayep-nav-sky);
                    }

                    /* PAGINATION */

                    .cayep-history-card
                        .ant-pagination
                        .ant-pagination-item-active {
                        border-color:
                            var(--cayep-nav-blue);

                        background:
                            var(--cayep-nav-blue);
                    }

                    .cayep-history-card
                        .ant-pagination
                        .ant-pagination-item-active
                        a {
                        color:
                            #ffffff;
                    }

                    .cayep-history-card
                        .ant-pagination
                        .ant-pagination-item:hover {
                        border-color:
                            var(--cayep-nav-sky);
                    }

                    .cayep-history-card
                        .ant-pagination
                        .ant-pagination-item:hover
                        a {
                        color:
                            var(--cayep-nav-sky);
                    }

                    /* FOOTER */

                    .cayep-page-footer {
                        display: flex;

                        align-items: center;
                        justify-content: center;

                        flex-wrap: wrap;

                        gap: 9px;

                        padding:
                            25px 10px 8px;

                        color:
                            var(--nav-muted);

                        font-size: 9px;
                    }

                    .cayep-footer-brand {
                        display: inline-flex;

                        align-items: center;

                        gap: 6px;

                        color:
                            var(--cayep-nav-navy);

                        font-weight: 800;
                    }

                    .cayep-footer-brand svg {
                        color:
                            var(--cayep-nav-orange);
                    }

                    .cayep-footer-separator {
                        color:
                            var(--cayep-nav-antique);
                    }

                    /* RESPONSIVE */

                    @media (max-width: 1100px) {
                        .cayep-stat-grid {
                            grid-template-columns:
                                repeat(
                                    3,
                                    1fr
                                );
                        }
                    }

                    @media (max-width: 850px) {
                        .cayep-stat-grid {
                            grid-template-columns:
                                1fr;
                        }

                        .cayep-stat-card
                            .ant-statistic {
                            justify-content:
                                flex-start;
                        }
                    }

                    @media (max-width: 768px) {
                        .certificate-dashboard {
                            padding: 16px;
                        }

                        .cayep-hero {
                            min-height:
                                auto;

                            border-radius:
                                16px;
                        }

                        .cayep-hero-content {
                            min-height:
                                auto;

                            padding:
                                28px 23px;

                            align-items:
                                flex-start;

                            flex-direction:
                                column;

                            gap: 18px;
                        }

                        .cayep-brand-mark {
                            width: 65px;
                            height: 65px;

                            flex-basis:
                                65px;

                            border-radius:
                                15px;

                            font-size:
                                30px;
                        }

                        .cayep-hero-title.ant-typography {
                            font-size:
                                29px;
                        }

                        .cayep-hero-description {
                            font-size:
                                13px;
                        }

                        .cayep-hero-meta {
                            align-items:
                                flex-start;

                            flex-direction:
                                column;

                            gap: 8px;
                        }

                        .cayep-meta-divider {
                            display:
                                none !important;
                        }

                        .cayep-generator-card
                            .ant-card-body,
                        .cayep-history-card
                            .ant-card-body {
                            padding:
                                21px;
                        }

                        .cayep-history-header {
                            align-items:
                                flex-start;
                        }
                    }

                    @media (max-width: 480px) {
                        .certificate-dashboard {
                            padding: 11px;
                        }

                        .cayep-hero-content {
                            padding:
                                24px 18px;
                        }

                        .cayep-eyebrow {
                            font-size:
                                9px;

                            letter-spacing:
                                1.2px;
                        }

                        .cayep-hero-title.ant-typography {
                            font-size:
                                25px;
                        }

                        .cayep-history-header {
                            flex-direction:
                                column;
                        }

                        .cayep-history-count {
                            align-self:
                                flex-start;
                        }

                        .cayep-card-heading {
                            gap: 10px;
                        }

                        .cayep-page-footer {
                            line-height:
                                1.7;
                        }
                    }
                `}
            </style>

            <div className="certificate-dashboard">
                <div className="cayep-dashboard-shell">

                    <section className="cayep-hero">
                        <div className="cayep-hero-pattern" />

                        <div className="cayep-hero-content">

                            <div className="cayep-brand-mark">
                                <SafetyCertificateOutlined />
                            </div>

                            <div className="cayep-hero-copy">

                                <div className="cayep-eyebrow">
                                    CAYEP • LEADERSHIP TRAINING
                                </div>

                                <Title
                                    className="cayep-hero-title"
                                >
                                    Graduation Certificates
                                </Title>

                                <Text className="cayep-hero-description">
                                    Manage, generate and issue
                                    professional certificates
                                    recognizing successful
                                    completion of CAYEP
                                    leadership training
                                    programs.
                                </Text>

                                <div className="cayep-hero-meta">

                                    <span>
                                        <CalendarOutlined />

                                        Training Year:

                                        <strong>
                                            {" "}
                                            {selectedYear
                                                ? getDisplayValue(
                                                      selectedYear
                                                  )
                                                : "Not selected"}
                                        </strong>
                                    </span>

                                    <span className="cayep-meta-divider" />

                                    <span>
                                        <CrownOutlined />

                                        Leadership
                                        Development
                                        Certification
                                    </span>

                                </div>

                            </div>

                        </div>
                    </section>

                    <section className="cayep-stat-grid">

                        <Card className="cayep-stat-card">

                            <Statistic
                                title="Certificates Generated"
                                value={
                                    certificates.length
                                }
                                prefix={
                                    <div className="cayep-stat-icon gold">
                                        <SafetyCertificateOutlined />
                                    </div>
                                }
                            />

                            <Text className="cayep-stat-note">
                                Current training year
                            </Text>

                        </Card>

                        <Card className="cayep-stat-card">

                            <Statistic
                                title="Graduates Loaded"
                                value={
                                    students.length
                                }
                                prefix={
                                    <div className="cayep-stat-icon blue">
                                        <TeamOutlined />
                                    </div>
                                }
                            />

                            <Text className="cayep-stat-note">
                                Selected training program
                            </Text>

                        </Card>

                        <Card className="cayep-stat-card">

                            <Statistic
                                title="Programs Available"
                                value={
                                    gradeClasses.length
                                }
                                prefix={
                                    <div className="cayep-stat-icon green">
                                        <ApartmentOutlined />
                                    </div>
                                }
                            />

                            <Text className="cayep-stat-note">
                                Leadership training programs
                            </Text>

                        </Card>

                    </section>

                    <Row
                        gutter={[
                            24,
                            24,
                        ]}
                        align="stretch"
                    >

                        <Col
                            xs={24}
                            lg={8}
                        >

                            <Card
                                className="cayep-generator-card"
                                bordered={false}
                            >

                                <div className="cayep-card-heading">

                                    <div className="cayep-card-icon">
                                        <FileProtectOutlined />
                                    </div>

                                    <div>

                                        <Title level={4}>
                                            Certificate Generator
                                        </Title>

                                        <Text>
                                            Select a training
                                            program and graduate
                                            to prepare an official
                                            certificate.
                                        </Text>

                                    </div>

                                </div>

                                <Divider />

                                <div className="cayep-form-section">

                                    <label className="cayep-field-label">
                                        TRAINING PROGRAM / CLASS
                                    </label>

                                    <Select
                                        className="cayep-select"
                                        placeholder="Select training program"
                                        value={
                                            selectedClass?.id
                                        }
                                        onChange={
                                            handleClassChange
                                        }
                                        showSearch
                                        optionFilterProp="children"
                                        allowClear
                                        onClear={() => {
                                            setSelectedClass(
                                                null
                                            );

                                            setSelectedStudent(
                                                null
                                            );

                                            setStudents([]);
                                        }}
                                    >

                                        {gradeClasses.map(
                                            (item) => (
                                                <Option
                                                    key={
                                                        item.id
                                                    }
                                                    value={
                                                        item.id
                                                    }
                                                >
                                                    {
                                                        getGradeClassLabel(
                                                            item
                                                        )
                                                    }
                                                </Option>
                                            )
                                        )}

                                    </Select>

                                </div>

                                <div className="cayep-form-section">

                                    <label className="cayep-field-label">
                                        GRADUATE
                                    </label>

                                    <Select
                                        className="cayep-select"
                                        placeholder={
                                            selectedClass
                                                ? "Select graduate"
                                                : "Select a program first"
                                        }
                                        value={
                                            selectedStudent
                                        }
                                        loading={
                                            studentLoading
                                        }
                                        disabled={
                                            !selectedClass
                                        }
                                        onChange={
                                            setSelectedStudent
                                        }
                                        showSearch
                                        optionFilterProp="children"
                                        allowClear
                                    >

                                        {students.map(
                                            (student) => (
                                                <Option
                                                    key={
                                                        student.id
                                                    }
                                                    value={
                                                        student.id
                                                    }
                                                >
                                                    {
                                                        getStudentName(
                                                            student
                                                        )
                                                    }
                                                </Option>
                                            )
                                        )}

                                    </Select>

                                    {selectedClass &&
                                        !studentLoading &&
                                        students.length ===
                                            0 && (
                                            <div className="cayep-inline-empty">
                                                No graduates are
                                                available for this
                                                program and
                                                training year.
                                            </div>
                                        )}

                                </div>

                                <div className="cayep-action-stack">

                                    <Button
                                        className="cayep-primary-button"
                                        type="primary"
                                        block
                                        size="large"
                                        icon={
                                            <DownloadOutlined />
                                        }
                                        onClick={
                                            generateStudentCertificate
                                        }
                                        disabled={
                                            !selectedStudent
                                        }
                                    >
                                        Generate Student
                                        Certificate
                                    </Button>

                                    <Button
                                        className="cayep-secondary-button"
                                        block
                                        size="large"
                                        icon={
                                            <TeamOutlined />
                                        }
                                        onClick={
                                            generateClassCertificates
                                        }
                                        disabled={
                                            !selectedClass ||
                                            students.length ===
                                                0
                                        }
                                    >
                                        Generate All Graduate
                                        Certificates
                                    </Button>

                                </div>

                                <Alert
                                    className="cayep-generator-notice"
                                    type="info"
                                    showIcon
                                    icon={
                                        <SafetyCertificateOutlined />
                                    }
                                    message="Official certificate workflow"
                                    description="Certificates are generated using the selected graduate, training program and active training year."
                                />

                            </Card>

                        </Col>

                        <Col
                            xs={24}
                            lg={16}
                        >

                            <Card
                                className="cayep-history-card"
                                bordered={false}
                            >

                                <div className="cayep-history-header">

                                    <div>

                                        <div className="cayep-section-kicker">
                                            RECORDS & ARCHIVE
                                        </div>

                                        <Title level={4}>
                                            Certificate History
                                        </Title>

                                        <Text>
                                            Review certificates
                                            generated during the
                                            selected training year.
                                        </Text>

                                    </div>

                                    <div className="cayep-history-count">

                                        <span>
                                            {
                                                certificates.length
                                            }
                                        </span>

                                        <small>
                                            Records
                                        </small>

                                    </div>

                                </div>

                                <Divider />

                                {loading ? (
                                    <div className="cayep-loading">

                                        <Spin size="large" />

                                        <Text>
                                            Loading certificate
                                            records...
                                        </Text>

                                    </div>
                                ) : (
                                    <Table
                                        className="cayep-certificate-table"
                                        columns={columns}
                                        dataSource={
                                            certificates
                                        }
                                        rowKey="id"
                                        scroll={{
                                            x: 720,
                                        }}
                                        pagination={{
                                            pageSize: 10,
                                            showSizeChanger:
                                                true,
                                            pageSizeOptions: [
                                                "10",
                                                "20",
                                                "50",
                                            ],
                                            showTotal:
                                                (
                                                    total,
                                                    range
                                                ) =>
                                                    `${range[0]}-${range[1]} of ${total} certificates`,
                                        }}
                                        locale={{
                                            emptyText: (
                                                <Empty
                                                    image={
                                                        <div className="cayep-empty-icon">
                                                            <SafetyCertificateOutlined />
                                                        </div>
                                                    }
                                                    description={
                                                        <div className="cayep-table-empty">
                                                            <Text strong>
                                                                No certificates
                                                                generated yet
                                                            </Text>

                                                            <Text>
                                                                Generated
                                                                certificates
                                                                will appear
                                                                here for the
                                                                selected
                                                                training
                                                                year.
                                                            </Text>
                                                        </div>
                                                    }
                                                />
                                            ),
                                        }}
                                    />
                                )}

                            </Card>

                        </Col>

                    </Row>

                    <footer className="cayep-page-footer">

                        <div className="cayep-footer-brand">
                            <SafetyCertificateOutlined />

                            <span>
                                CAYEP Leadership Training
                            </span>
                        </div>

                        <span className="cayep-footer-separator">
                            •
                        </span>

                        <span>
                            Professional Certificate
                            Administration
                        </span>

                    </footer>

                </div>
            </div>
        </>
    );
};

export default CertificateDashboard;

